import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, TextField, Grid, DialogActions, Button, DialogTitle, Divider, makeStyles, } from "@material-ui/core";
import { useFormState } from "../../../../hooks/useFormState";
import { useDispatch, useSelector } from "react-redux";
import { loadRoles, loadUserRoles, submitInternalMessage } from "../../../../redux/actions";
import { usersService } from "../../../../services";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { Autocomplete } from "@material-ui/lab";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { selectCaseRoles, selectUserRoles, userSelector } from "../../../../redux/selectors";
import { allCaseRoles } from "../../../../auth/CaseRoles";
const useStyles = makeStyles({
    helperText: {
        fontSize: "1em",
    },
    messageCount: {
        fontSize: "1em",
        marginRight: "14px",
        marginLeft: "14px",
        color: "rgba(0, 0, 0, 0.54)",
    },
});
export const InternalMessageDialog = ({ isDialogOpen, onDialogClose, caseId, }) => {
    const dispatch = useDispatch();
    const dialogStyles = useStyles();
    const user = useSelector(userSelector);
    const userHasGeneralPermissions = useMemo(() => {
        return !!user && user.roles.map(r => r.toString()).some(x => allCaseRoles.includes(x));
    }, [user]);
    const { formProps, triggerSubmit, triggerReset, state, handleOnChange, formPropsMultipleFileInput, } = useFormState({
        upns: [],
        subject: "",
        message: "",
    }, ["upns", "message", "subject"], ({ state }) => {
        dispatch(submitInternalMessage(caseId, state.values));
        onDialogClose();
        triggerReset();
    });
    const [upns, setUpns] = useState([]);
    const [limitedUpns, setLimitedUpns] = useState([]);
    // WIP: Case roles only for case-role-based users
    const caseRoles = useSelector(selectCaseRoles);
    const userRoles = useSelector(selectUserRoles);
    useEffect(() => {
        if (userHasGeneralPermissions) {
            usersService.getUsers().then(response => setUpns(response.data));
        }
        else if (!!user) {
            dispatch(loadRoles(caseId));
            dispatch(loadUserRoles());
        }
    }, [dispatch, user, userHasGeneralPermissions, caseId]);
    useEffect(() => {
        if (!!user && !userHasGeneralPermissions) {
            const combinedLists = [
                ...caseRoles.map(x => ({ id: x.userId, name: x.userId })),
                ...userRoles.map(x => ({ id: x.upn, name: x.upn }))
            ].reduce((acc, curr) => {
                if (acc.some(a => a.id.toLowerCase() === curr.id.toLowerCase()))
                    return acc;
                return [...acc, curr];
            }, []).sort((a, b) => a.id.localeCompare(b.id));
            setLimitedUpns(combinedLists);
        }
    }, [userRoles, caseRoles, userHasGeneralPermissions, user]);
    const [messageError, setMessageError] = useState(false);
    const handleOnClickValidation = () => {
        setMessageError(false);
        if (state.values["message"].length === 0) {
            setMessageError(true);
            return;
        }
        else {
            triggerSubmit();
        }
    };
    return (React.createElement(Dialog, { open: isDialogOpen, onClose: () => {
            onDialogClose();
            triggerReset();
        }, maxWidth: "md", fullWidth: true },
        React.createElement(DialogTitle, null, "Internal message"),
        React.createElement(Divider, null),
        React.createElement(DialogContent, null,
            React.createElement("form", null,
                React.createElement(Grid, { container: true, spacing: 3 },
                    React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(Autocomplete, { multiple: true, autoSelect: true, freeSolo: true, options: userHasGeneralPermissions
                                ? upns
                                : limitedUpns, ChipProps: { color: "primary" }, renderInput: (params) => {
                                var _a, _b;
                                return (React.createElement(TextField, Object.assign({}, params, { name: "links", variant: "outlined", label: "Recipients*", error: !!((_a = state.fieldMeta["upns"]) === null || _a === void 0 ? void 0 : _a.error), helperText: (_b = state.fieldMeta["upns"]) === null || _b === void 0 ? void 0 : _b.error })));
                            }, getOptionLabel: option => option.id, value: state.values["upns"], onChange: (e, data) => handleOnChange({ name: "upns", value: data }), "data-test": "recipients" })),
                    React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(TextField, Object.assign({ variant: "outlined" }, formProps("subject"), { label: "Subject*", fullWidth: true, "data-test": "internal-message-subject", inputProps: { maxLength: 250 }, helperText: `${state.values.subject.length}/${250}`, FormHelperTextProps: {
                                className: dialogStyles.helperText,
                            } }))),
                    React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement("label", { style: messageError ? { color: "#f44336" } : {} }, "Message*:"),
                        React.createElement(MarkdownEditor, { onChange: (value) => {
                                handleOnChange({ name: "message", value });
                                setMessageError(false);
                            }, value: state.values["message"], dataTest: "internal-message-message" }),
                        React.createElement("label", { className: dialogStyles.messageCount },
                            React.createElement("div", { style: messageError ? { color: "#f44336", display: "flex" } : {} },
                                state.values.message.length,
                                "/",
                                5000))),
                    React.createElement(Grid, { item: true, xs: 12, sm: 12 },
                        React.createElement(FileInput, Object.assign({}, formPropsMultipleFileInput("file"), { id: "text-button-file", multiple: true })))))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { onClick: () => handleOnClickValidation(), "data-test": "internalMessageSendButton", color: "primary" }, "Send"))));
};
